import React from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { translateService, signService } from "../../services/imports";
import signers from "../../services/signerManagementService";
import { LoadingButton } from "../../components/formcontrols/imports";
import CertificateFields, {
  UnitField,
  emptyCertificate,
  checkCertificate,
  certificateBody,
  subjectFieldOf,
  vettedSubjectRefused,
} from "./certificateFields";

const t = (k, ...a) => translateService.t(`signers.certificate.${k}`, ...a);

const dialog = (isOpen, onClose, title, body, footer) => (
  <Modal isOpen={!!isOpen} fade={false} className={`signer-dialog ${translateService.key}`} toggle={onClose}>
    <ModalHeader>
      <span>{title}</span>
    </ModalHeader>
    <ModalBody>
      <div className="signer-dialog__scroll">{body}</div>
      <div className="buttons signer-dialog__footer">{footer}</div>
    </ModalBody>
  </Modal>
);

const queueOrProblem = (e) =>
  signService.problemCode(e) === "SIGNER_QUEUE_UNAVAILABLE"
    ? translateService.t("signers.queueUnavailable")
    : signService.problemMessage(e, translateService.t("signers.actionError"));

// "Request certificate" for a cloud signing tool that never had a request
// (NONE: made before certificates): its certification and subject from the
// workspace's certification options (read when the dialog opens; unavailable
// ones disabled with their reason), and the name in the certificate,
// prefilled from the signing tool's name. The gateway answers 202 at once;
// the page then polls.
//
// props: isOpen, workspaceId, signer, kind (the catalogue's entry),
// catalogue, onClose(), onSaved(signer)
export class RequestCertificateDialog extends React.Component {
  state = { value: emptyCertificate(this.props.kind), options: null, optionsError: null, errors: {}, sending: false, refusal: null };

  componentDidMount() {
    if (this.props.isOpen) this.open();
  }

  componentDidUpdate(prev) {
    if (this.props.isOpen && !prev.isOpen) this.open();
  }

  componentWillUnmount() {
    this.closed = true;
  }

  open() {
    const { kind, signer } = this.props;
    this.setState({ value: emptyCertificate(kind, signer && signer.name), errors: {}, sending: false, refusal: null });
    this.loadOptions();
  }

  loadOptions = () => {
    this.setState({ options: null, optionsError: null });
    signers.certificationOptions(this.props.workspaceId).subscribe(
      (res) => !this.closed && this.setState({ options: res.data || null }),
      (e) => !this.closed && this.setState({ optionsError: signService.problemMessage(e, translateService.t("signers.actionError")) })
    );
  };

  send = () => {
    const { kind, catalogue, workspaceId, signer } = this.props;
    const { value, options } = this.state;
    const errors = {
      ...checkCertificate(kind, value, catalogue, options, "certification"),
      ...checkCertificate(kind, value, catalogue, options, "subject"),
      ...checkCertificate(kind, value, catalogue, options, "unit"),
    };
    if (Object.keys(errors).length) return this.setState({ errors });
    this.setState({ sending: true, refusal: null });
    signers.requestCertificate(workspaceId, signer.uuid, certificateBody(value, catalogue, options)).subscribe(
      (res) => !this.closed && (this.setState({ sending: false }), this.props.onSaved(res.data)),
      (e) => {
        if (this.closed) return;
        const code = signService.problemCode(e);
        const detail = queueOrProblem(e);
        this.setState({ sending: false });
        // no longer open to the workspace: the options again, it shows disabled
        if (code === "CERTIFICATION_NOT_AVAILABLE") {
          this.loadOptions();
          return this.setState({ refusal: detail, value: { ...value, certification: "" } });
        }
        // the organization's records changed while the dialog was open
        if (vettedSubjectRefused(code, e.response && e.response.data, options))
          return this.setState({ refusal: translateService.t("signers.subject.changed") });
        if (code === "ORGANIZATION_NAME_TOO_LONG") return this.setState({ errors: { organizationName: detail } });
        const member = (code === "SUBJECT_REQUIRED" || code === "PARAMETER_IS_INVALID") && subjectFieldOf(e.response && e.response.data);
        if (member) return this.setState({ errors: { [member]: detail } });
        this.setState({ refusal: detail });
      }
    );
  };

  renderBody() {
    const { kind, catalogue } = this.props;
    const { value, options, optionsError, errors } = this.state;
    if (optionsError)
      return (
        <p className="signer-dialog__error" role="alert">
          {optionsError}{" "}
          <button type="button" className="signer-dialog__link" onClick={this.loadOptions}>
            {translateService.t("signature.retry")}
          </button>
        </p>
      );
    if (!options || !kind) return <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />;
    // a workspace of no organization: cloud signing tools are for organizations
    if (options.standing === "NO_ORGANIZATION") return <p>{translateService.t("signers.noOption")}</p>;
    const change = (patch) => this.setState((s) => ({ value: { ...s.value, ...patch }, errors: {} }));
    return (
      <React.Fragment>
        <CertificateFields kind={kind} catalogue={catalogue} options={options} value={value} errors={errors} onChange={change} />
        <UnitField value={value} error={errors.organizationalUnit} onChange={change} />
      </React.Fragment>
    );
  }

  render() {
    const { isOpen, onClose, signer } = this.props;
    const open = this.state.options && this.state.options.standing !== "NO_ORGANIZATION";
    return dialog(
      isOpen,
      onClose,
      t("requestTitle", (signer && signer.name) || ""),
      <React.Fragment>
        {this.renderBody()}
        {this.state.refusal && (
          <p className="signer-dialog__error" role="alert">
            {this.state.refusal}
          </p>
        )}
      </React.Fragment>,
      <React.Fragment>
        {open && (
          <LoadingButton type="button" className="button background-color-two color-white" loading={this.state.sending} onClick={this.send}>
            {t("requestSend")}
          </LoadingButton>
        )}
        <LoadingButton type="button" className="button background-color-one color-white" disabled={this.state.sending} onClick={onClose}>
          {translateService.t("signers.cancel")}
        </LoadingButton>
      </React.Fragment>
    );
  }
}

// "Upload certificate": the certificate the organization's PKI issued for the
// request, with its chain - the PKI's file as it is (application/x-pem-file)
// or pasted PEM ({"pem"}). A refused check (CERTIFICATE_INVALID) keeps the
// form open with the gateway's description; a request a newer one replaced
// meanwhile reloads the card.
//
// props: isOpen, workspaceId, signer, onClose(), onSaved(signer), onReload()
export class UploadCertificateDialog extends React.Component {
  state = { file: null, pem: "", sending: false, refusal: null };

  componentDidUpdate(prev) {
    if (this.props.isOpen && !prev.isOpen) this.setState({ file: null, pem: "", sending: false, refusal: null });
  }

  componentWillUnmount() {
    this.closed = true;
  }

  send = () => {
    const { file, pem } = this.state;
    if (!file && !pem.trim()) return this.setState({ refusal: t("uploadEmpty") });
    this.setState({ sending: true, refusal: null });
    signers.uploadCertificate(this.props.workspaceId, this.props.signer.uuid, file ? { file } : { pem: pem.trim() }).subscribe(
      (res) => !this.closed && (this.setState({ sending: false }), this.props.onSaved(res.data)),
      (e) => {
        if (this.closed) return;
        const body = (e && e.response && e.response.data) || {};
        if (signService.problemCode(e) === "CERTIFICATE_REQUEST_NOT_FOUND" && body.reason === "replaced") {
          this.setState({ sending: false });
          return this.props.onReload(t("replaced"));
        }
        this.setState({ sending: false, refusal: signService.problemMessage(e, translateService.t("signers.actionError")) });
      }
    );
  };

  render() {
    const { isOpen, onClose, signer } = this.props;
    return dialog(
      isOpen,
      onClose,
      t("uploadTitle", (signer && signer.name) || ""),
      <React.Fragment>
        <p className="signer-dialog__hint">{t("uploadHint")}</p>
        <label className="signer-dialog__field" htmlFor="signer-certificate-file">
          <span>{t("file")}</span>
          <input
            id="signer-certificate-file"
            type="file"
            accept=".pem,.crt,.cer,.txt,application/x-pem-file"
            onChange={(e) => this.setState({ file: (e.target.files && e.target.files[0]) || null, refusal: null })}
          />
        </label>
        <label className="signer-dialog__field" htmlFor="signer-certificate-pem">
          <span>{t("paste")}</span>
          <textarea
            id="signer-certificate-pem"
            rows={6}
            spellCheck="false"
            disabled={!!this.state.file}
            placeholder="-----BEGIN CERTIFICATE-----"
            value={this.state.pem}
            onChange={(e) => this.setState({ pem: e.target.value, refusal: null })}
          />
        </label>
        {this.state.refusal && (
          <p className="signer-dialog__error" role="alert">
            {this.state.refusal}
          </p>
        )}
      </React.Fragment>,
      <React.Fragment>
        <LoadingButton type="button" className="button background-color-two color-white" loading={this.state.sending} onClick={this.send}>
          {t("uploadSend")}
        </LoadingButton>
        <LoadingButton type="button" className="button background-color-one color-white" disabled={this.state.sending} onClick={onClose}>
          {translateService.t("signers.cancel")}
        </LoadingButton>
      </React.Fragment>
    );
  }
}
